import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  PenTool,
  LifeBuoy,
  TrendingUp,
} from "lucide-react";
import Hero from "../components/Hero";
import TrustBadges from "../components/TrustBadges";
import WhyUs from "../components/WhyUs";
import TechStack from "../components/TechStack";
import QuickCheck from "../components/QuickCheck";
import Showcase from "../components/Showcase";
import usePageMeta from "../hooks/usePageMeta";

const FOUNDER_IMG = "/founder-warm.jpg";

const services = [
  {
    icon: PenTool,
    title: "Website-Erstellung",
    teaser: "Vom One-Pager bis zum vollwertigen Auftritt — strategisch gedacht, sauber gebaut.",
    bullets: ["UX & UI Design", "Mobile-First", "Performance & SEO"],
    href: "/leistungen",
  },
  {
    icon: LifeBuoy,
    title: "Verwaltung & Support",
    teaser: "Hosting, Updates, Backups, kleine Änderungen. Ich kümmere mich, Du arbeitest am Business.",
    bullets: ["Tägliche Backups", "Updates & Monitoring", "Reaktion < 24h"],
    href: "/preise",
  },
  {
    icon: TrendingUp,
    title: "Reichweite & SEO",
    teaser: "Sichtbar werden bei Google & Co. — damit Kunden Dich überhaupt finden.",
    bullets: ["Lokale SEO", "Google Business", "Conversion-Tracking"],
    href: "/leistungen",
  },
];

export default function HomePage() {
  usePageMeta(
    "Webdesign Karlsruhe — Websites zum Festpreis ab 799 € | NovaSite",
    "Webdesign aus Karlsruhe: Ich baue Deine Website persönlich — vom One-Pager bis zum Online-Shop. Festpreise, Wartung, Hosting & lokale SEO für Selbstständige und kleine Unternehmen.",
    "/"
  );
  return (
    <>
      {/* 1 — Hero (stark, persönlich) */}
      <Hero />

      {/* 2 — Vertrauensleiste */}
      <TrustBadges />

      {/* 3 — Meine Leistungen */}
      <section
        data-testid="home-services"
        className="py-20 md:py-28 bg-zinc-50/60"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">
                Meine Leistungen
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-zinc-900 mt-4 leading-[1.05]">
                Bauen. Pflegen. <br />
                <span className="italic">Wachsen lassen.</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-zinc-500 leading-relaxed">
              Drei Bereiche — eine Person. Ich begleite Dich vom ersten
              Entwurf bis zur ersten Kundenanfrage über Google. Und dann
              weiter.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`home-service-${i}`}
                  className="group relative bg-white border border-zinc-100 rounded-3xl p-7 md:p-8 hover:-translate-y-1 hover:shadow-[0_24px_64px_-24px_rgba(0,0,0,0.15)] hover:border-zinc-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-zinc-900 text-white flex items-center justify-center group-hover:bg-emerald-500 transition-colors duration-300">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl font-medium text-zinc-900 mt-6">
                    {s.title}
                  </h3>
                  <p className="text-sm text-zinc-500 mt-3 leading-relaxed">
                    {s.teaser}
                  </p>
                  <ul className="mt-5 space-y-1.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2 text-sm text-zinc-700"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-900" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={s.href}
                    data-testid={`home-service-link-${i}`}
                    className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900 group-hover:gap-3 transition-all"
                  >
                    Mehr erfahren <ArrowUpRight size={15} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4 — Warum mit mir arbeiten */}
      <WhyUs />

      {/* 5 — Tech-Stack (nur 1x) */}
      <TechStack />

      {/* 5b — Vorher/Nachher Showcase */}
      <Showcase />

      {/* 6 — Über mich (Teaser mit Foto) */}
      <section
        data-testid="home-about-teaser"
        className="py-20 md:py-28 bg-zinc-50/60"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="relative">
              <div
                aria-hidden
                className="absolute -inset-3 bg-gradient-to-br from-emerald-100/60 to-amber-100/40 rounded-[2rem] blur-2xl opacity-70"
              />
              <div className="relative aspect-[4/5] rounded-[1.75rem] overflow-hidden border border-zinc-100 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]">
                <picture>
                  <source srcSet="/founder-warm.webp" type="image/webp" />
                  <img
                    src={FOUNDER_IMG}
                    alt="Fabian Winkler, Solo-Entwickler aus Karlsruhe"
                    width="1040"
                    height="992"
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                    style={{ objectPosition: "50% 20%" }}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-zinc-200 px-3.5 py-1.5 text-[11px] font-semibold tracking-widest uppercase text-zinc-700">
              Über mich
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-zinc-900 mt-5 leading-[1.05]">
              Hinter NovaSite steht <br />
              <span className="italic">eine</span> Person — ich.
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-xl">
              Mein Name ist Fabian, 22, aus Karlsruhe. Ich code seit
              Schultagen und baue heute Websites für Selbstständige und
              kleine Unternehmen, die online endlich Wirkung zeigen wollen.
            </p>
            <p className="mt-4 text-zinc-500 leading-relaxed max-w-xl">
              Kein Vertrieb, keine endlosen Abstimmungsphasen. Wenn Du mit
              mir arbeitest, arbeitest Du <em>mit mir</em> — direkt und
              ehrlich.
            </p>
            <Link
              to="/ueber-mich"
              data-testid="home-about-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white text-zinc-900 text-sm font-medium px-6 py-3 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-all"
            >
              Mehr über mich <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 7 — Kostenloser Website-Check */}
      <QuickCheck />

      {/* 7b — Lokale SEO-Sektion */}
      <section data-testid="home-local-seo" className="py-16 md:py-20 border-t border-zinc-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-zinc-900">
            Webdesign aus Karlsruhe — für Selbstständige &amp; kleine Unternehmen
          </h2>
          <p className="mt-5 text-zinc-500 leading-relaxed">
            Ob Handwerksbetrieb, Praxis, Kanzlei, Salon oder Start-up: Ich erstelle professionelle
            Websites für Kunden aus <strong className="text-zinc-700 font-medium">Karlsruhe, Ettlingen, Bruchsal,
            Rastatt, Baden-Baden und Pforzheim</strong> — und remote für ganz Deutschland. Von der{" "}
            <Link to="/leistungen" className="underline underline-offset-4 hover:no-underline text-zinc-700">Erstellung</Link>{" "}
            über <Link to="/preise" className="underline underline-offset-4 hover:no-underline text-zinc-700">Wartung &amp; Hosting</Link>{" "}
            bis zur lokalen SEO, damit Dich Kunden bei Google finden.
          </p>
        </div>
      </section>

      {/* 8 — Abschließender CTA */}
      <section data-testid="home-final-cta" className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="relative overflow-hidden rounded-[2rem] md:rounded-[2.5rem] bg-zinc-900 text-white p-10 md:p-20">
            <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] bg-emerald-500/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-[22rem] h-[22rem] bg-amber-500/10 rounded-full blur-3xl" />

            <div className="relative max-w-2xl">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 font-semibold">
                Limitierte Kapazität · 2 Slots frei ab Juni 2026
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-5 leading-[1.05]">
                Bereit für Deine <br />
                <span className="italic">neue Website?</span>
              </h2>
              <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
                Buche einen kostenlosen 20-Minuten Call. Keine Verpflichtung,
                kein Verkaufsdruck — nur ehrliche Beratung von Mensch zu
                Mensch.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/kontakt"
                  data-testid="home-final-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-zinc-900 text-base font-medium px-7 py-4 hover:bg-zinc-100 transition-all hover:scale-[1.02]"
                >
                  Beratungstermin buchen
                  <ArrowUpRight
                    size={17}
                    className="group-hover:rotate-45 transition-transform duration-300"
                  />
                </Link>
                <Link
                  to="/preise"
                  data-testid="home-final-cta-secondary"
                  className="inline-flex items-center justify-center rounded-full border border-white/30 text-white text-base font-medium px-7 py-4 hover:bg-white/10 transition-all"
                >
                  Preise ansehen
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
